import React from "react";
import { motion } from "framer-motion";
import { fadeUp, stagger, viewport } from "./anim";

const principles = [
  {
    n: "01",
    title: "Human oversight, always.",
    body: "Every system I build has a person behind it — checking, refining, making sure what AI produces is actually right before it reaches you or your customers.",
  },
  {
    n: "02",
    title: "Right-sized, not oversized.",
    body: "Not every task needs the biggest, most expensive model running in the background. I match the tool to the job, so you're not paying for — or running — more than you need.",
  },
  {
    n: "03",
    title: "Transparent, always.",
    body: "Your customers will always know when they're talking to AI. No deception, no hiding the ball.",
  },
  {
    n: "04",
    title: "Purposeful, not wasteful.",
    body: "I don't believe in AI for AI's sake. If a simpler solution does the job better, that's what I build.",
  },
];

export const Manifesto = () => {
  return (
    <section id="manifesto" data-testid="manifesto-section" className="relative py-24 sm:py-32 overflow-hidden grain" style={{ background: "#0b0912" }}>
      <div className="absolute inset-0 hero-grid opacity-60" />
      <div className="absolute -top-20 left-1/3 w-[34rem] h-[34rem] rounded-full opacity-30 animate-blob" style={{ background: "radial-gradient(circle,#E933C6,transparent 65%)", filter: "blur(90px)" }} />
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div initial="hidden" whileInView="show" viewport={viewport} variants={stagger} className="max-w-3xl">
          <motion.div variants={fadeUp} className="font-mono-accent text-xs tracking-[0.25em] uppercase text-fuchsia-400 mb-4">
            Conscientious AI
          </motion.div>
          <motion.h2 variants={fadeUp} className="font-display font-bold text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1]">
            Conscientious AI. <span className="text-gradient">On purpose.</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-5 text-slate-300/90 text-lg leading-relaxed">
            AI is powerful — but powerful doesn't mean unlimited, and it doesn't mean unsupervised.
            I believe using AI well means using it <em className="text-fuchsia-300 not-italic font-semibold">on purpose</em>, not just because it's available.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          variants={stagger}
          className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px rounded-3xl overflow-hidden"
          style={{ background: "rgba(233,51,198,0.12)" }}
        >
          {principles.map((p, i) => (
            <motion.div
              key={p.n}
              variants={fadeUp}
              data-testid={`manifesto-principle-${i + 1}`}
              className="group relative p-8 sm:p-10 bg-[#0b0912] hover:bg-[#120c1c] transition-colors duration-500"
            >
              <div className="flex items-start gap-6">
                <span className="font-display font-extrabold text-5xl sm:text-6xl leading-none text-transparent"
                  style={{ WebkitTextStroke: "1.5px rgba(233,51,198,0.5)" }}>
                  {p.n}
                </span>
                <div>
                  <h3 className="font-display font-bold text-white text-xl sm:text-2xl tracking-tight group-hover:text-fuchsia-200 transition-colors">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-slate-400 leading-relaxed">{p.body}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Manifesto;
